import { useEffect } from "react";
import { Link } from "react-router-dom";
import { PublicShell } from "@/components/public/PublicChrome";

/* =============================================================================
 * FEATURES — same art direction as the landing (manuscript baseline, Amiri
 * for Arabic, Newsreader display, lapis/gold/harakat palette). Routed at
 * /features.
 *
 * Emoji icons are gone: each feature carries the Arabic word it relates to,
 * set in naskh. Live features and in-build features are the same list with
 * different ink, not two visually unrelated blocks.
 * ========================================================================== */

const LIVE = [
  {
    ar: "حروف",
    title: "Alphabet",
    desc: "All 28 letters in their four forms, plus the vowels, with guided practice.",
  },
  {
    ar: "مفردات",
    title: "Vocabulary",
    desc: "4,000+ words with audio and a real example sentence on every entry.",
  },
  {
    ar: "مراجعة",
    title: "Spaced repetition",
    desc: "Each item comes back on its own schedule, timed to the day you would forget it.",
  },
  {
    ar: "أخطاء",
    title: "My errors",
    desc: "Every mistake is queued and kept in rotation until it stops being a mistake.",
  },
  {
    ar: "واحة",
    title: "Oasis",
    desc: "Your oasis grows out of the reviews you complete. Decorations unlock as you go.",
  },
];

const IN_BUILD = [
  {
    ar: "قواعد",
    title: "Grammar",
    desc: "200 patterns taught through interactive challenges.",
  },
  {
    ar: "تصريف",
    title: "Conjugation",
    desc: "Verb forms drilled as a game rather than a table.",
  },
  {
    ar: "جذور",
    title: "Roots",
    desc: "Triliteral roots, and the words that grow out of each one.",
  },
  {
    ar: "قراءة",
    title: "Reading",
    desc: "Real texts with tap-to-look-up and comprehension questions.",
  },
  {
    ar: "أرقام",
    title: "Numbers",
    desc: "Arabic numerals trained by ear and by sight.",
  },
  {
    ar: "جملة",
    title: "Sentence builder",
    desc: "Assemble sentences and get the correction immediately.",
  },
  {
    ar: "قصة",
    title: "Story builder",
    desc: "Write from scratch with your own vocabulary, corrected by AI.",
  },
  {
    ar: "استماع",
    title: "Listening",
    desc: "Word and sentence challenges for the ear.",
  },
  {
    ar: "أصدقاء",
    title: "Social",
    desc: "Friends, shared oases and a leaderboard.",
  },
];

function FeatureRow({ f, pending }) {
  return (
    <div className={`ft-row${pending ? " pending" : ""}`}>
      <div className="ft-ar ar" aria-hidden="true">
        {f.ar}
      </div>
      <div>
        <h3>
          {f.title}
          {pending && <span className="ft-flag">in build</span>}
        </h3>
        <p>{f.desc}</p>
      </div>
    </div>
  );
}

export default function Features() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Harf — Features";
  }, []);

  return (
    <PublicShell className="hf-feat">
      <style>{`
@import url('https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&family=Newsreader:opsz,wght@6..72,400;6..72,500&family=Inter:wght@400;500;600&display=swap');

.hf-feat {
  --paper: #ECEDF1;
  --ink: #14172A;
  --ink-soft: #5D6179;
  --lapis: #1F3FA8;
  --gold: #B0862F;
  --harakat: #B8412E;
  --rule: #C9CCD6;
  background: var(--paper);
  color: var(--ink);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
}
.hf-feat * { box-sizing: border-box; }
.hf-feat h1, .hf-feat h2, .hf-feat h3 { font-family: 'Newsreader', Georgia, serif; font-weight: 500; letter-spacing: -0.015em; margin: 0; }
.hf-feat p { margin: 0; }
.hf-feat a { color: inherit; text-decoration: none; }
.hf-feat :focus-visible { outline: 2px solid var(--lapis); outline-offset: 3px; }
.hf-feat .ar { font-family: 'Amiri', 'Noto Naskh Arabic', serif; direction: rtl; }
.hf-feat .wrap { max-width: 1120px; margin: 0 auto; padding: 0 28px; position: relative; }
.hf-feat .tick { display: inline-block; width: 26px; height: 1px; background: var(--harakat); vertical-align: 6px; margin-right: 12px; }

/* HEAD */
.hf-feat .ft-head { padding: 104px 0 0; }
.hf-feat .ft-head-grid { display: grid; grid-template-columns: minmax(0, 6fr) minmax(0, 5fr); gap: 56px; align-items: end; }
.hf-feat .ft-head h1 { font-size: clamp(38px, 5.6vw, 64px); line-height: 1.04; letter-spacing: -0.03em; max-width: 13ch; }
.hf-feat .ft-head p { margin-top: 22px; color: var(--ink-soft); font-size: 16.5px; max-width: 48ch; }
.hf-feat .ft-glyph { font-size: clamp(120px, 17vw, 210px); line-height: .8; color: var(--lapis); opacity: .1; text-align: center; user-select: none; }
.hf-feat .ft-rule { margin-top: 52px; height: 1px; background: linear-gradient(90deg, var(--ink) 0 22%, var(--rule) 22% 100%); }

/* SECTIONS */
.hf-feat .band { padding: 72px 0; }
.hf-feat .band + .band { border-top: 1px solid var(--rule); }
.hf-feat .band h2 { font-size: clamp(24px, 3vw, 32px); margin-bottom: 6px; }
.hf-feat .band-note { color: var(--ink-soft); font-size: 14.5px; margin-bottom: 38px; max-width: 52ch; }

.hf-feat .ft-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 52px; }
.hf-feat .ft-row { display: grid; grid-template-columns: 84px 1fr; gap: 20px; align-items: baseline; padding: 22px 0; border-top: 1px solid var(--rule); }
.hf-feat .ft-ar { font-size: 22px; color: var(--lapis); line-height: 1.4; }
.hf-feat .ft-row h3 { font-size: 19px; line-height: 1.2; }
.hf-feat .ft-row p { font-size: 14.5px; color: var(--ink-soft); margin-top: 5px; max-width: 46ch; }
.hf-feat .ft-row.pending .ft-ar { color: var(--ink-soft); opacity: .55; }
.hf-feat .ft-flag { font-size: 11.5px; font-family: 'Inter', sans-serif; color: var(--gold); border: 1px solid var(--gold); border-radius: 2px; padding: 1px 6px; margin-left: 10px; vertical-align: 3px; }

/* CTA */
.hf-feat .cta { background: var(--ink); color: var(--paper); padding: 90px 0; text-align: center; }
.hf-feat .cta .ar-mark { font-size: 44px; color: var(--gold); opacity: .75; margin-bottom: 20px; }
.hf-feat .cta h2 { font-size: clamp(28px, 4vw, 42px); line-height: 1.1; max-width: 18ch; margin: 0 auto; }
.hf-feat .cta p { color: #A7ACC4; font-size: 15px; margin: 18px auto 0; max-width: 40ch; }
.hf-feat .btn-paper { display: inline-block; margin-top: 30px; background: var(--paper); color: var(--ink); padding: 15px 32px; border-radius: 2px; font-size: 15px; font-weight: 600; transition: background .18s ease; }
.hf-feat .btn-paper:hover { background: var(--gold); }

@media (max-width: 900px) {
  .hf-feat .ft-head { padding-top: 72px; }
  .hf-feat .ft-head-grid { grid-template-columns: 1fr; gap: 28px; }
  .hf-feat .ft-glyph { display: none; }
  .hf-feat .ft-grid { grid-template-columns: 1fr; gap: 0; }
  .hf-feat .band { padding: 54px 0; }
}
@media (prefers-reduced-motion: reduce) {
  .hf-feat * { animation: none !important; transition: none !important; }
}
            `}</style>

      <header className="ft-head">
        <div className="wrap">
          <div className="ft-head-grid">
            <div>
              <h1>Everything in Harf, and what is coming</h1>
              <p>
                Five things are live today. The rest is being built in the open,
                in the order learners keep asking for.
              </p>
            </div>
            <div className="ft-glyph ar" aria-hidden="true">
              حرف
            </div>
          </div>
          <div className="ft-rule" />
        </div>
      </header>

      <section className="band">
        <div className="wrap">
          <h2>
            <span className="tick" />
            Live now
          </h2>
          <p className="band-note">
            Available on every account from the day you sign up.
          </p>
          <div className="ft-grid">
            {LIVE.map((f) => (
              <FeatureRow key={f.title} f={f} />
            ))}
          </div>
        </div>
      </section>

      <section className="band">
        <div className="wrap">
          <h2>
            <span className="tick" />
            In build
          </h2>
          <p className="band-note">
            Shipping over the coming months. Premium and Lifetime cover all of
            it, with no upgrade to pay later.
          </p>
          <div className="ft-grid">
            {IN_BUILD.map((f) => (
              <FeatureRow key={f.title} f={f} pending />
            ))}
          </div>
        </div>
      </section>

      <section className="cta">
        <div className="wrap">
          <div className="ar-mark ar" aria-hidden="true">
            ابدأ
          </div>
          <h2>Start with the alphabet today</h2>
          <p>Seven days of full access, no card.</p>
          <Link to="/auth?mode=signup" className="btn-paper">
            Start learning
          </Link>
        </div>
      </section>
    </PublicShell>
  );
}
